import { createFileRoute, Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { FileText, LayoutDashboard, List, LogOut, Mail, Menu, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAdminAuth } from '@/contexts/AdminAuthContext';

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
  beforeLoad: () => {
    // Auth check happens in component - we need AdminAuthProvider higher up
    return {};
  },
});

function AdminLayout() {
  const { isAuthenticated, isLoading, admin, logout } = useAdminAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isLoginPage = location.pathname === '/admin/login';
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (isLoading) return;
    if (!isAuthenticated && !isLoginPage) {
      navigate({ to: '/admin/login' });
    }
  }, [isAuthenticated, isLoading, isLoginPage, navigate]);

  // Close sidebar on route change (mobile/tablet)
  useEffect(() => {
    void location.pathname;
    setSidebarOpen(false);
  }, [location.pathname]);

  // Login page - no layout wrapper
  if (isLoginPage) {
    return <Outlet />;
  }

  // Loading or not authenticated
  if (isLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500" />
      </div>
    );
  }

  const navLinks = (
    <>
      <Link
        to="/admin"
        className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
        activeOptions={{ exact: true }}
        activeProps={{ className: 'bg-purple-500/20 text-purple-400' }}
      >
        <LayoutDashboard className="size-5 shrink-0" />
        Dashboard
      </Link>
      <Link
        to="/admin/posts"
        className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
        activeOptions={{ exact: true }}
        activeProps={{ className: 'bg-purple-500/20 text-purple-400' }}
      >
        <FileText className="size-5 shrink-0" />
        Bài viết
      </Link>
      <Link
        to="/admin/posts/categories"
        className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
        activeProps={{ className: 'bg-purple-500/20 text-purple-400' }}
      >
        <List className="size-5 shrink-0" />
        Chuyên mục
      </Link>
      <Link
        to="/admin/contacts"
        className="flex items-center gap-3 px-3 py-2 rounded-lg text-gray-300 hover:bg-gray-800 hover:text-white transition-colors"
        activeProps={{ className: 'bg-purple-500/20 text-purple-400' }}
      >
        <Mail className="size-5 shrink-0" />
        Contact
      </Link>
    </>
  );

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="min-h-screen bg-gray-950 flex">
        {/* Mobile overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-hidden="true"
          />
        )}

        <aside
          className={`
          fixed lg:static inset-y-0 left-0 z-50 w-64 bg-gray-900 border-r border-gray-800 flex flex-col
          transform transition-transform duration-200 ease-out
          lg:translate-x-0
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        >
          <div className="p-4 lg:p-6 border-b border-gray-800 flex items-center justify-between">
            <Link to="/" className="text-xl font-bold text-white">
              AITOS
            </Link>
            <button
              type="button"
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-2 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white"
              aria-label="Đóng menu"
            >
              <X className="size-5" />
            </button>
          </div>
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">{navLinks}</nav>
        </aside>

        <div className="flex-1 flex flex-col min-w-0">
          <header className="h-14 lg:h-16 bg-gray-900 border-b border-gray-800 flex items-center justify-between px-4 lg:px-6 shrink-0">
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white"
              aria-label="Mở menu"
            >
              <Menu className="size-5" />
            </button>
            <h2 className="text-gray-300 font-medium text-sm lg:text-base flex-1 lg:flex-none">
              Quản trị
            </h2>
            <div className="flex items-center gap-2 lg:gap-4">
              <span className="text-xs lg:text-sm text-gray-400 truncate max-w-[120px] lg:max-w-none">
                {admin?.name}
              </span>
              <button
                type="button"
                onClick={logout}
                className="flex items-center gap-2 px-2 lg:px-3 py-2 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors text-sm"
              >
                <LogOut className="size-4 shrink-0" />
                <span className="hidden sm:inline">Đăng xuất</span>
              </button>
            </div>
          </header>
          <main className="flex-1 p-4 lg:p-6 overflow-auto min-h-0">
            <Outlet />
          </main>
        </div>
      </div>
    </>
  );
}
